import React, { useState, useRef } from 'react';
import {
  AnalysisWindow, setAnalysisWindow, setBalanceAnchor,
  exportarDados, resetarConta, PALAVRA_DE_CONFIRMACAO,
} from '../services/api';
import { formatBRL } from '../utils/format';
import './RestartModal.css';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';

/**
 * O convite para recomeçar, depois de meses sem lançar.
 *
 * ── Nenhuma das duas opções apaga nada ───────────────────────────────────
 *
 * "Recomeçar do zero" grava uma DATA DE CORTE: as análises passam a enxergar
 * só o que vem dela em diante. O lançamento antigo continua no banco, e ela
 * pode desfazer amanhã. Apagar seria irreversível para resolver um problema
 * que uma data resolve.
 *
 * ── Por que o custo aparece antes ────────────────────────────────────────
 *
 * `estimateVariableSpend` exige 3 meses COM lançamento. Cortar abaixo disso
 * não deixa a projeção imprecisa — deixa a projeção SEM estimativa, mostrando
 * só conta fixa e parcela. É literalmente o bug que fazia ela mentir otimista
 * em ~R$ 2.300/mês antes de setembro.
 *
 * Uma tela que oferece "começar limpo" sem dizer isso está vendendo alívio e
 * entregando um número pior. Por isso o preview vem do servidor e o aviso é
 * inevitável, não uma nota de rodapé.
 */

interface Props {
  janela: AnalysisWindow;
  onDone: () => void;
  onDismiss: () => void;
}

const RestartModal: React.FC<Props> = ({ janela, onDone, onDismiss }) => {
  useTravarScroll();
  const caixa = useRef<HTMLDivElement>(null);
  useFocoNoModal(caixa);

  const [escolha, setEscolha] = useState<'corte' | 'saldo' | 'apagar' | null>(null);
  const [saldo, setSaldo] = useState('');
  const [palavra, setPalavra] = useState('');
  const [baixando, setBaixando] = useState(false);
  const [baixado, setBaixado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  /* Digitar a palavra, e não só clicar. Um clique é pouco para algo sem volta,
     e o servidor exige a mesma palavra — o botão não é a trava, é o aviso. */
  const podeApagar = palavra.trim().toUpperCase() === PALAVRA_DE_CONFIRMACAO;

  const hoje = new Date();
  const hojeISO = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
  const valor = parseFloat(saldo.replace(/\./g, '').replace(',', '.'));
  const valorOk = !Number.isNaN(valor);

  const confirmar = async () => {
    if (!escolha || salvando) return;
    if (escolha === 'apagar' && !podeApagar) return;
    setSalvando(true);
    setErro(null);
    try {
      if (escolha === 'apagar') {
        await resetarConta(palavra.trim().toUpperCase());
      } else {
        if (valorOk) await setBalanceAnchor({ amount: valor, date: hojeISO });
        // Só o "corte" grava a janela. "Saldo" mantém o histórico visível —
        // é a diferença inteira entre as duas primeiras opções.
        await setAnalysisWindow(escolha === 'corte' ? hojeISO : null);
      }
      onDone();
    } catch (e: any) {
      setErro(e?.response?.data?.error ?? 'Não consegui salvar. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  const p = janela.preview;

  return (
    <div className="rm-fundo" role="dialog" aria-modal="true" aria-labelledby="rm-titulo" ref={caixa}>
      <div className="rm">
        <h2 id="rm-titulo">Faz {janela.staleMonths} meses desde seu último lançamento</h2>
        <p className="rm-sub">
          Os números do app ainda usam esse histórico. Dá para recomeçar do zero,
          ou só acertar o saldo e manter o que você já lançou.
        </p>

        <button
          className={`rm-opcao${escolha === 'saldo' ? ' ativa' : ''}`}
          onClick={() => setEscolha('saldo')}
        >
          <span className="rm-opcao-titulo">Só acertar o saldo</span>
          <span className="rm-opcao-desc">
            Seus {janela.totalTransactions} lançamentos continuam contando nas análises.
            O saldo passa a partir de hoje, e a estimativa de gasto continua funcionando.
          </span>
        </button>

        <button
          className={`rm-opcao${escolha === 'corte' ? ' ativa' : ''}`}
          onClick={() => setEscolha('corte')}
        >
          <span className="rm-opcao-titulo">Recomeçar do zero</span>
          <span className="rm-opcao-desc">
            As análises passam a enxergar só o que você lançar de hoje em diante.
            {' '}<strong>{p.hiddenCount} lançamentos</strong> saem das contas, mas
            nada é apagado, e dá para desfazer depois.
          </span>
          {escolha === 'corte' && p.losesEstimate && (
            <span className="rm-custo">
              Com isso você fica sem estimativa de gasto variável até acumular 3 meses
              novos. Até lá, a projeção mostra só contas fixas e parcelas, e aparece
              mais otimista do que a realidade.
            </span>
          )}
        </button>

        <button
          className={`rm-opcao rm-opcao-perigo${escolha === 'apagar' ? ' ativa' : ''}`}
          onClick={() => setEscolha('apagar')}
        >
          <span className="rm-opcao-titulo">Apagar tudo e começar de novo</span>
          <span className="rm-opcao-desc">
            Volta ao estado de conta nova: apaga os {janela.totalTransactions} lançamentos,
            suas categorias, métodos de pagamento, metas, investimentos, lembretes e as
            regras aprendidas. As categorias padrão voltam.
          </span>
          {escolha === 'apagar' && (
            <span className="rm-custo rm-custo-perigo">
              Isto não tem desfazer. Não existe lixeira e o app não guarda backup,
              então baixe seus dados antes se quiser conseguir consultá-los depois.
            </span>
          )}
        </button>

        {escolha === 'apagar' && (
          <div className="rm-apagar">
            <button
              className="rm-baixar"
              onClick={async () => {
                setBaixando(true);
                try { await exportarDados(); setBaixado(true); }
                catch { setErro('Não consegui gerar o arquivo.'); }
                finally { setBaixando(false); }
              }}
              disabled={baixando}
              aria-busy={baixando}
            >
              {baixado ? '✓ Backup baixado' : 'Baixar meus dados antes'}
            </button>

            <label htmlFor="rm-palavra">
              Digite <strong>{PALAVRA_DE_CONFIRMACAO}</strong> para confirmar
            </label>
            <input
              id="rm-palavra"
              value={palavra}
              onChange={e => setPalavra(e.target.value)}
              placeholder={PALAVRA_DE_CONFIRMACAO}
              autoComplete="off"
              disabled={salvando}
            />
          </div>
        )}

        {escolha && escolha !== 'apagar' && (
          <div className="rm-saldo">
            <label htmlFor="rm-saldo-input">Quanto você tem hoje? <em>(opcional)</em></label>
            <input
              id="rm-saldo-input"
              inputMode="decimal"
              placeholder="0,00"
              value={saldo}
              onChange={e => setSaldo(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmar()}
              disabled={salvando}
              autoFocus
            />
            {valorOk && <span className="rm-saldo-eco">{formatBRL(valor)}</span>}
          </div>
        )}

        {erro && <div className="rm-erro">{erro}</div>}

        <div className="rm-acoes">
          <button className="rm-agora-nao" onClick={onDismiss} disabled={salvando}>
            Agora não
          </button>
          <button
            className={`rm-confirmar${escolha === 'apagar' ? ' perigo' : ''}`}
            onClick={confirmar}
            disabled={!escolha || salvando || (escolha === 'apagar' && !podeApagar)}
          >
            {/* Sem escolha o rótulo não pode nomear uma delas: sugeria uma
                decisão que ela não tomou. */}
            {escolha === 'apagar' ? 'Apagar tudo'
              : escolha === 'corte' ? 'Recomeçar'
              : escolha === 'saldo' ? 'Acertar saldo'
              : 'Escolha uma opção'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestartModal;
